import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { resolveImageUrl, responsiveImg } from '../../lib/imageUrl';
import { useOpening } from '../../lib/opening';

const ease = [0.22, 1, 0.36, 1];

const useIsMobile = () => {
  const [m, setM] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const q = window.matchMedia('(max-width: 767px)');
    const on = () => setM(q.matches);
    q.addEventListener('change', on);
    return () => q.removeEventListener('change', on);
  }, []);
  return m;
};

const HeroMedia = ({ cfg, mobile, reduce }) => {
  const [ready, setReady] = useState(false);
  const f = mobile ? cfg.focusMobile : cfg.focusDesktop;
  const pos = { objectPosition: `${f.x}% ${f.y}%` };
  const video = cfg.videoEnabled && cfg.videoUrl && !reduce;
  return (
    <>
      <img {...responsiveImg(cfg.posterUrl, '100vw', { eager: true })} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={pos} data-testid="hero-poster" />
      {video && (
        <video
          key={cfg.videoUrl}
          src={resolveImageUrl(cfg.videoUrl)}
          poster={resolveImageUrl(cfg.posterUrl)}
          autoPlay muted loop playsInline preload="auto"
          onPlaying={() => setReady(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1800ms] ease-out ${ready ? 'opacity-100' : 'opacity-0'}`}
          style={pos}
          data-testid="hero-video"
        />
      )}
    </>
  );
};

const ScrollCue = ({ label, onClick }) => (
  <motion.button
    type="button" onClick={onClick}
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.6, delay: 2.4 }}
    className="group mt-14 md:mt-16 inline-flex items-center gap-5 text-[11px] uppercase tracking-[0.35em] text-white/85 hover:text-white transition-colors focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-8 focus-visible:outline-white"
    data-testid="hero-scroll-cue"
  >
    <span className="relative block w-px h-12 bg-white/30 overflow-hidden">
      <span className="absolute inset-x-0 top-0 h-1/2 bg-white animate-[heroLine_2.6s_ease-in-out_infinite]" />
    </span>
    {label}
  </motion.button>
);

const CinematicHero = () => {
  const [params] = useSearchParams();
  const preview = params.get('apercu') === '1';
  const cfg = useOpening(preview);
  const mobile = useIsMobile();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.06]);
  const veil = useTransform(scrollYProgress, [0, 1], [0, 0.55]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -90]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);
  const next = () => window.scrollTo({ top: ref.current.offsetTop + ref.current.offsetHeight - (document.querySelector('[data-testid=main-navbar]')?.offsetHeight || 0), behavior: reduce ? 'auto' : 'smooth' });
  const shade = (cfg?.shade ?? 30) / 100;

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-[#111111]" data-testid="home-hero">
      <motion.div style={{ scale }} className="absolute inset-0 will-change-transform">
        {cfg && <HeroMedia cfg={cfg} mobile={mobile} reduce={reduce} />}
      </motion.div>
      <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(180deg, rgba(17,17,17,${shade * 0.7}) 0%, rgba(17,17,17,${shade * 0.35}) 40%, rgba(17,17,17,${Math.min(shade * 1.6, 0.85)}) 100%)` }} />
      <motion.div style={{ opacity: veil }} className="absolute inset-0 bg-[#111111] pointer-events-none" />
      {cfg && (
        <motion.div style={{ y: textY, opacity: textOpacity }} className="relative z-10 h-full container-custom flex flex-col justify-end pb-16 md:pb-24">
          <motion.h1
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 2, delay: 0.9, ease }}
            className="font-serif font-light text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.08] tracking-[-0.01em] max-w-4xl"
            style={{ textWrap: 'balance' }}
            data-testid="hero-slogan"
          >
            {cfg.sloganTop}
            {cfg.sloganBottom && <><br />{cfg.sloganBottom}</>}
          </motion.h1>
          {cfg.scrollLabel && <ScrollCue label={cfg.scrollLabel} onClick={next} />}
        </motion.div>
      )}
      {cfg?.isDraft && (
        <div className="absolute top-28 right-4 md:right-10 z-20 bg-white text-[#111111] text-[10px] uppercase tracking-[0.3em] px-4 py-2" data-testid="hero-preview-badge">
          Aperçu — brouillon non publié
        </div>
      )}
    </section>
  );
};

export default CinematicHero;
